import { Event } from '@livecontrol/scheduler/model';
import { DateTime } from 'luxon';
import type { ReactElement } from 'react';
import { useEffect, useMemo, useRef, useState } from 'react';
import styled, { css } from 'styled-components';
import type { ScheduledEvent } from '../event-details';
import { useUpdateEventAdminFields } from '../event-details';
import { BitrateChart } from './bitrate-chart';
import type { EventPhase, MuxRange } from './mux-health';
import { bitrateStats, formatDuration } from './mux-health';
import { useMuxHealth } from './use-mux-health';

interface Props {
  scheduledEvent: ScheduledEvent;
}

interface Stat {
  title: string;
  value: string;
  /** Secondary text after the value (e.g. `Kbps`). */
  unit?: string;
}

const Scroll = styled.div`
  overflow-x: auto;
`;

// Themed resize grip (and scrollbar corner) so the native light handle doesn't clash.
const resizer = css`
  &::-webkit-resizer {
    background: repeating-linear-gradient(
      135deg,
      transparent 0 3px,
      rgba(176, 186, 209, 0.35) 3px 4px
    );
  }

  &::-webkit-scrollbar-corner {
    background: transparent;
  }
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(132px, 1fr));
  gap: 1px;
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 10px;
  overflow: hidden;
  background: rgba(255, 255, 255, 0.06);
`;

const StatCell = styled.div`
  padding: 15px 12px 16px;
  background: rgb(13, 20, 38);

  .title {
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: rgba(176, 186, 209, 0.5);
    margin-bottom: 9px;
    white-space: nowrap;
    font-weight: 600;
    font-size: 10px;
  }

  .value {
    font-weight: 800;
    font-stretch: normal;
    font-size: 27px;
    line-height: 1;
    letter-spacing: -0.01em;
    color: rgb(240, 242, 245);
    white-space: nowrap;
  }

  .unit {
    margin-left: 5px;
    color: rgba(176, 186, 209, 0.55);
    white-space: nowrap;
    font-weight: 600;
    font-size: 10px;
    letter-spacing: 0;
  }
`;

const MetricsNote = styled.div`
  font-size: 14px;
  color: rgba(255, 255, 255, 0.4);
`;

const Notes = styled.div`
  .title {
    margin: 20px 0px 10px;
    font-weight: 600;
    font-size: 13px;
    color: rgba(176, 186, 209, 0.6);
  }

  /*
   * A div rather than the textarea this used to be. Dropping the cap is not enough on a form
   * control: it keeps the height it is given and scrolls the rest out of sight. Nothing was ever
   * saved from it either, so it only looked editable.
   */
  .notes-text,
  .notes-empty {
    width: 100%;
    background: rgba(8, 14, 30, 0.5);
    border: 1px solid rgba(255, 255, 255, 0.1);
    border-radius: 8px;
    padding: 10px;
    font-size: 14px;
    font-weight: 400;
    line-height: 1.55;
    min-height: 72px;
    white-space: pre-wrap;
    overflow-wrap: anywhere;
  }

  .notes-text {
    color: rgba(213, 221, 238, 0.92);
  }

  .notes-empty {
    color: rgba(176, 186, 209, 0.4);
    font-style: italic;
  }
`;

const Vip = styled.div`
  display: flex;
  flex-direction: column;
  margin-top: 16px;
`;

const MarkButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  align-self: flex-start;
  padding: 8px 13px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 9px;
  background: transparent;
  color: rgba(176, 186, 209, 0.7);
  font-weight: 600;
  font-size: 12px;
  cursor: pointer;
  transition: all var(--transition);

  &:hover,
  &:focus {
    outline: none;
    color: rgba(213, 221, 238, 0.92);
    border-color: rgba(255, 255, 255, 0.2);
  }
`;

const VipSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 9px;
  padding: 13px 15px;
  border: 1px solid rgba(255, 80, 95, 0.3);
  border-radius: 11px;
  background: rgba(255, 80, 95, 0.08);

  .head {
    display: flex;
    align-items: center;
    gap: 8px;
    background: transparent;
    border: none;
    padding: 0;
    cursor: pointer;
  }

  .check {
    width: 18px;
    height: 18px;
    flex-shrink: 0;
    border-radius: 5px;
    display: grid;
    place-items: center;
    border: 1.5px solid rgb(255, 80, 95);
    background: rgb(255, 80, 95);
    color: rgb(255, 255, 255);
  }

  .check svg {
    display: block;
  }

  .label {
    font-weight: 700;
    font-size: 13px;
    color: rgb(240, 242, 245);
  }
`;

const VipTextarea = styled.textarea`
  width: 100%;
  background: rgba(8, 14, 30, 0.5);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  color: rgba(213, 221, 238, 0.92);
  padding: 10px;
  font-size: 13px;
  font-weight: 400;
  line-height: 1.55;
  min-height: 54px;
  resize: vertical;
  ${resizer}

  &:focus {
    outline: none;
    border-color: rgba(255, 255, 255, 0.2);
  }
`;

const CheckIcon = (): ReactElement => (
  <svg viewBox='0 0 24 24' width='11' height='11' fill='none' stroke='currentColor' strokeWidth={3}>
    <path d='M5 13l4 4L19 7' />
  </svg>
);

const phaseOf = (status?: string | null): EventPhase => {
  if (status === Event.Phase.Live.toString()) {
    return 'live';
  }

  if (status === Event.Phase.Upcoming.toString()) {
    return 'upcoming';
  }

  return 'past';
};

const VIP_PLACEHOLDER = 'Board members attending; client requested extra care on audio.';

export const HomeTab = ({ scheduledEvent }: Props): ReactElement => {
  const phase = phaseOf(scheduledEvent.status);
  const supportNotes = (scheduledEvent.location?.user?.support_notes ?? '').trim();

  /*
   * Straight to this event's live stream in Mux rather than the dashboard root, which is where
   * the button went while nothing filled this in. Same URL the Performance screen builds; the
   * organisation and environment ids are hardcoded there too.
   *
   * Undefined without a stream id, so BitrateChart falls back to the root instead of linking to
   * a monitor page for nothing.
   */
  const muxDashboardUrl = scheduledEvent.location?.mux_stream_id
    ? `https://dashboard.mux.com/organizations/g91c68/environments/599vti/video/live-streams/${scheduledEvent.location.mux_stream_id}/monitor`
    : undefined;

  // Chart range drives which ingest window we fetch (Live=5m … Full=whole event).
  const [range, setRange] = useState<MuxRange>('live');

  // Uptime counts in real time instead of jumping with the 10s ingest poll.
  const [nowMs, setNowMs] = useState(() => Date.now());

  /*
   * Real Mux health while live, and for a finished event as long as Mux still
   * has its ingest data (roughly a day). See use-mux-health.ts.
   */
  const { health, loading: healthLoading, error: healthError } = useMuxHealth(
    scheduledEvent,
    phase,
    range
  );

  /*
   * Three states for a finished event with an empty series, and conflating them
   * is what made the "no data" note flash before the chart: still fetching, the
   * fetch failed, or Mux really has nothing left. Only the last one says anything
   * about the event.
   */
  const noSeries = health.series.length === 0;
  const pastEmpty = phase === 'past' && noSeries;
  const pastWithoutData = pastEmpty && !healthLoading && healthError === undefined;

  /*
   * Live shares the first two: until the first response lands there is nothing
   * to draw, and a grid of zeroes reads as a dead stream rather than as us still
   * asking.
   */
  const metricsLoading = (pastEmpty || phase === 'live') && noSeries && healthLoading;

  const metricsFailed =
    (pastEmpty || phase === 'live') && noSeries && !healthLoading && healthError !== undefined;

  const [updateEvent] = useUpdateEventAdminFields();
  const [highPriority, setHighPriority] = useState(!!scheduledEvent.high_priority);
  const [note, setNote] = useState(scheduledEvent.high_priority_note ?? '');
  const noteTimer = useRef<ReturnType<typeof setTimeout>>();

  useEffect(
    () => (): void => {
      if (noteTimer.current) {
        clearTimeout(noteTimer.current);
      }
    },
    []
  );

  useEffect(() => {
    if (phase !== 'live') {
      return undefined;
    }

    const timer = setInterval((): void => {
      setNowMs(Date.now());
    }, 1000);

    return (): void => {
      clearInterval(timer);
    };
  }, [phase]);

  const stats = useMemo<Stat[]>(() => {
    const series = bitrateStats(health.series);

    const elapsed =
      health.streamStartMs === undefined
        ? '—'
        : formatDuration((nowMs - health.streamStartMs) / 1000);

    if (phase === 'live') {
      return [
        { title: 'Bitrate · Ingest', value: health.videoKbps.toLocaleString(), unit: 'Kbps' },
        { title: 'Uptime', value: elapsed },
        { title: 'Viewers', value: health.viewers.toLocaleString() },
        { title: 'Dips', value: health.dips.toString() },
        { title: 'Drops', value: health.drops.toString() }
      ];
    }

    /*
     * For a finished event the duration is the span its ingest series covers:
     * `streamStartMs` measures against now, which is meaningless once it ended.
     */
    const firstAt = DateTime.fromISO(health.series[0]?.time ?? '');
    const lastAt = DateTime.fromISO(health.series[health.series.length - 1]?.time ?? '');

    const covered =
      firstAt.isValid && lastAt.isValid
        ? formatDuration(lastAt.diff(firstAt, 'seconds').seconds)
        : '—';

    return [
      { title: 'Avg Bitrate · Ingest', value: series.avg.toLocaleString(), unit: 'Kbps' },
      { title: 'Peak', value: series.peak.toLocaleString(), unit: 'Kbps' },
      { title: 'Duration', value: covered },
      { title: 'Dips', value: health.dips.toString() },
      { title: 'Drops', value: health.drops.toString() }
    ];
  }, [health, phase, nowMs]);

  const eventId = Number(scheduledEvent.id);

  const setVip = (value: boolean): void => {
    setHighPriority(value);

    if (!value && noteTimer.current) {
      clearTimeout(noteTimer.current);
    }

    void updateEvent({ id: eventId, highPriority: value });
  };

  const changeNote = (value: string): void => {
    setNote(value);

    if (noteTimer.current) {
      clearTimeout(noteTimer.current);
    }

    noteTimer.current = setTimeout(() => {
      void updateEvent({ id: eventId, highPriorityNote: value });
    }, 2000);
  };

  const metrics =
    phase === 'upcoming' ? (
      <MetricsNote>Live metrics appear here once the event is live (Mux).</MetricsNote>
    ) : metricsLoading ? (
      <MetricsNote>Loading ingest data from Mux…</MetricsNote>
    ) : metricsFailed ? (
      <MetricsNote>Couldn&apos;t load ingest data from Mux for this event.</MetricsNote>
    ) : pastWithoutData ? (
      <MetricsNote>
        Mux no longer has ingest data for this event.
      </MetricsNote>
    ) : (
      <>
        <Scroll className='cc-scroll'>
          <StatsGrid>
            {stats.map((stat) => (
              <StatCell key={stat.title}>
                <div className='title'>{stat.title}</div>
                <div className='value'>
                  {stat.value}
                  {stat.unit !== undefined && <span className='unit'>{stat.unit}</span>}
                </div>
              </StatCell>
            ))}
          </StatsGrid>
        </Scroll>
        {/* Range pills only make sense while it's still running; a past event has one window. */}
        <BitrateChart
          series={health.series}
          range={range}
          onRangeChange={setRange}
          showRanges={phase === 'live'}
          dashboardUrl={muxDashboardUrl}
        />
      </>
    );

  return (
    <>
      {metrics}
      <Notes>
        <div className='title'>Support Notes</div>
        {supportNotes ? (
          <div className='notes-text'>{supportNotes}</div>
        ) : (
          <div className='notes-empty'>No support notes.</div>
        )}
      </Notes>

      <Vip>
        {highPriority ? (
          <VipSection>
            <button
              type='button'
              className='head'
              onClick={(): void => {
                setVip(false);
              }}
            >
              <span className='check'>
                <CheckIcon />
              </span>
              <span className='label'>VIP Event</span>
            </button>
            <VipTextarea
              value={note}
              placeholder={VIP_PLACEHOLDER}
              onChange={(event): void => {
                changeNote(event.target.value);
              }}
            />
          </VipSection>
        ) : (
          <MarkButton
            type='button'
            onClick={(): void => {
              setVip(true);
            }}
          >
            <i className='fas fa-plus' /> Mark as VIP Event
          </MarkButton>
        )}
      </Vip>
    </>
  );
};
